/**
 * Komórka wartości w AdvancedFiltersDialog: Input albo Select z opcjami z filtra (konfiguracja per kolumna).
 */

import React, { FC, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from '@mui/material';
import { Options } from 'react-select';
import WiseSelect from 'components/wise/WiseSelect';
import { useLoadData } from 'plugins/api/endpoints/useLoadData';
import { IWiseTableFilter, IOption } from 'components/wise/types';

interface IFilterValueCellProps {
  filter: IWiseTableFilter | null;
  value: string;
  onChange: (value: string) => void;
}

const FilterValueCell: FC<IFilterValueCellProps> = ({ filter, value, onChange }) => {
  const { t } = useTranslation();
  const { data: apiOptions, refetch: refetchApiOptions } = useLoadData(
    filter?.dictionary?.values_get_u_i_api || '',
    { page: 1, limit: 999 },
    { enabled: false }
  );

  useEffect(() => {
    if (filter?.dictionary?.values_get_u_i_api) {
      refetchApiOptions();
    }
  }, [filter?.dictionary?.values_get_u_i_api, refetchApiOptions]);

  const options: Options<IOption<{ value: string | number; text: string }>> = useMemo(() => {
    if (!filter?.dictionary) return [];
    const fromApi = apiOptions?.items?.map((item: { value: string | number; text: string }) => ({
      value: item.value,
      label: item.text,
      item
    }));
    if (fromApi?.length) return fromApi;
    const fromDict = filter.dictionary.values?.map((item: any) => {
      const val =
        item.value != null && typeof item.value === 'object'
          ? String((item.value as { translation?: string }).translation ?? item.value)
          : (item.value as string | number);
      return { value: val, label: item.text, item: { value: val, text: item.text } };
    });
    return fromDict || [];
  }, [filter?.dictionary, apiOptions?.items]);

  const hasOptions = options.length > 0;

  if (filter && hasOptions) {
    return (
      <WiseSelect<{ value: string | number; text: string }>
        styleGroup="portal"
        mode="singlechoice"
        placeholder={filter.label || t('Wartość')}
        options={options}
        value={value || null}
        onChange={(item) => onChange(item?.value != null ? String(item.value) : '')}
        clearable
      />
    );
  }

  return (
    <TextField
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      placeholder={t('Wartość')}
      style={{ width: '100%' }}
      sx={{
        '& .MuiInputBase-root': { height: 38, minHeight: 38, borderRadius: 0 },
        '& .MuiOutlinedInput-input': {
          py: 0,
          height: 38,
          boxSizing: 'border-box',
          fontSize: 12,
          foncolor: '#000'
        }
      }}
    />
  );
};

export default FilterValueCell;
